import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api-service';
import { Button, LoadingOverlay, TextInput } from '@mantine/core';
import { LOGIN_PATH } from '@/lib/navigation';
import logoUrl from '/images/favicon.svg';

export default function OnboardingPage(): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [tenantName, setTenantName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async () => {
        if (!email || !password || !tenantName) {
            setError('Please fill in all fields');
            return;
        }

        setIsLoading(true);
        setError('');
        try {
            const payload = {
                tenant_data: { name: tenantName },
                user_data: { 
                    email: email, 
                    password: password,
                    name: email.split('@')[0] // Provide a default name from email
                }
            };
            const res = await api.public.onboarding(payload);
            if (res.error) throw new Error(res.error as string);

            // Redirect to login page and prepopulate email
            navigate(`${LOGIN_PATH}?email=${encodeURIComponent(email)}`);
        } catch (err: any) {
            setError(err.message || 'Error occurred during onboarding.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col relative">
            <LoadingOverlay visible={isLoading} loaderProps={{ size: 'md', color: 'black' }} />

            {/* Logo in top left */}
            <div className="p-6 flex items-end gap-2">
                <img src={logoUrl} className="w-10" alt="Logo" />
                <div className="text-3xl font-semibold text-gray-900">
                    Turizi
                </div>
            </div>

            {/* Centered form */}
            <div className="flex-1 flex items-center justify-center">
                <div className="w-full max-w-md px-8">
                    <div className="space-y-6">
                        <div>
                            <h1 className="text-2xl font-normal text-gray-900">
                                {t('onboarding_title')}
                            </h1>
                            <p className="text-sm text-gray-600 mt-2">
                                {t('onboarding_description')}
                            </p>
                        </div>

                        {error && (
                            <div className="p-4 text-sm text-red-800 rounded-lg bg-red-50 border border-red-200">
                                {t(error)}
                            </div>
                        )}

                        <div className="space-y-4">
                            <TextInput
                                label={t('workspace_name')}
                                id="tenantName"
                                name="tenantName"
                                type="text"
                                value={tenantName}
                                onChange={(e) => setTenantName(e.target.value)}
                                autoFocus
                                size="lg"
                            />
                            <TextInput
                                label={t('email')}
                                id="email"
                                name="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                                size="lg"
                            />
                            <TextInput
                                label={t('password')}
                                id="password"
                                name="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSubmit();
                                    }
                                }}
                                autoComplete="new-password"
                                size="lg"
                            />
                            <div className="pt-2">
                                <Button
                                    fullWidth
                                    size="md"
                                    color="black"
                                    onClick={handleSubmit}
                                >
                                    {t('create_workspace', 'Create Workspace')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
